import { DateTime, Interval } from "luxon";

export const SECOND = 1000;
export const MINUTE = SECOND * 60;
export const HOUR = MINUTE * 60;
export const DAY = HOUR * 24;
export const WEEK = DAY * 7;
export const MONTH_30 = DAY * 30;
export const MONTH_31 = DAY * 31;
export const YEAR = DAY * 365;

// convertDate - returns user friendly date
export function convertDate(millis : string) : string {
    const date = DateTime.fromMillis(parseFloat(millis));
    const duration = Interval.fromDateTimes(date, DateTime.now());
    if (duration.length("days") > 10) {
        return date.toLocaleString(DateTime.DATETIME_SHORT);
    }
    return date.toRelativeCalendar() as string;
}

// getCurrentYear - return current year
export function getCurrentYear(): number {
    return DateTime.now().year;
}

export function timeToHours(value: string): number {
    const parts = value.split(":");
    return (Number.parseInt(parts[0], 10) * 60) + Number.parseInt(parts[1], 10);
}

export function hoursToTime(value?: number): string {
    if (value === 0) {
        return "00:00";
    }
    if (!value) {
        return "";
    }
    const hour = Math.floor(value / 60).toString().padStart(2, "0");
    const minutes = (value % 60).toString().padStart(2, "0");
    return `${hour}:${minutes}`;
}

export function toUTCInputDate(date: Date): string {
    return [
        date.getUTCFullYear(),
        (date.getUTCMonth() + 1).toString().padStart(2, "0"),
        date.getUTCDate().toString().padStart(2, "0"),
    ].join("-");
}

export function toInputDate(date: Date): string {
    return [
        date.getFullYear(),
        (date.getMonth() + 1).toString().padStart(2, "0"),
        date.getDate().toString().padStart(2, "0"),
    ].join("-");
}

export function toInputHourMinute(date: Date): string {
    return [
        date.getHours().toString().padStart(2, "0"),
        date.getMinutes().toString().padStart(2, "0"),
    ].join(":");
}

export function toUTCInputHourMinute(date: Date): string {
    return [
        date.getHours().toString().padStart(2, "0"),
        date.getMinutes().toString().padStart(2, "0"),
    ].join(":");
}
export function toInputTime(date: Date): string {
    return [
        date.getHours().toString().padStart(2, "0"),
        date.getMinutes().toString().padStart(2, "0"),
        date.getSeconds().toString().padStart(2, "0"),
    ].join(":");
}

export function toInputDateTime(date: Date): string {
    return `${toInputDate(date)}T${toInputTime(date)}`;
}

export function toInputDateHourMinute(date: Date): string {
    return `${toInputDate(date)}T${toInputHourMinute(date)}`;
}

export function getTimeZoneOffset(timeZone: string, date: Date = new Date()) {
    // Use Japan for the standard year/month/day locale
    const [year, month, day, hour, minute, second] = date.toLocaleString("ja", {
        timeZone,
    }).split(/[/\s:]/).map(value => parseInt(value, 10));
    const timeInTimezone = Date.UTC(year, month - 1, day, hour, minute, second);
    const currentTimezone = new Date(date).setMilliseconds(0);
    return (timeInTimezone - currentTimezone) / MINUTE;
}

export function getDateInTimeZone(localDate: Date, fromTimezone: string, timeZone: string) {
    const fromOffset = getTimeZoneOffset(fromTimezone, localDate);
    const toOffset = getTimeZoneOffset(timeZone, localDate);
    return new Date(localDate.getTime() + (fromOffset - toOffset) * MINUTE);
}

export function calculateDateOffset(offset: number, dayNumber: number, currentDate: Date, hours = 0, minutes = 0, seconds = 0) {
    let nextDay = dayNumber - currentDate.getDay();
    if (nextDay <= 0) {
        nextDay += offset;
    }

    return new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate() + nextDay, hours, minutes, seconds);
}

export const Time = {
    SECOND,
    MINUTE,
    HOUR,
    DAY,
    WEEK,
    MONTH_30,
    MONTH_31,
    YEAR,

    getDateInTimeZone,
};

export function validateMinimumDateAllowed(minDate: Date, localDate: Date) {
    if (minDate.getTime() > localDate.getTime()) {
        return new Date(minDate.getTime() + Time.MINUTE);
    }

    return localDate;
}
